import { useI18n } from '@ecom/i18n';
import {
  Badge,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { KpiCard } from '@/features/dashboard/components/KpiCard';
import { formatCatalogPrice } from '@/features/products/format-price';
import { useCouponsQuery } from '@/features/promotions/api';
import type { Coupon } from '@/features/promotions/types';

const TAG_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M20 12.5 12.5 20a1.5 1.5 0 0 1-2.12 0L4 13.62a1.5 1.5 0 0 1 0-2.12L11.5 4H18a2 2 0 0 1 2 2v6.5Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <circle cx="15.5" cy="8.5" r="1.25" fill="currentColor" />
  </svg>
);

const CHECK_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 13l4 4L19 7"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RECEIPT_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 3h12v18l-2.5-1.5L13 21l-2.5-1.5L8 21l-2-1.5V3Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M9 8h6M9 12h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

function isCurrentlyActive(coupon: Coupon): boolean {
  if (!coupon.isActive) return false;
  if (coupon.validUntil === null) return true;
  return new Date(coupon.validUntil).getTime() >= Date.now();
}

function usageRateLabel(coupon: Coupon, t: ReturnType<typeof useI18n>['t']): string {
  if (coupon.usageLimitTotal === null) {
    return t('merchant.marketing.usageUnlimited', { count: coupon.usageCount });
  }
  const rate = coupon.usageLimitTotal > 0 ? coupon.usageCount / coupon.usageLimitTotal : 0;
  return `${String(coupon.usageCount)} / ${String(coupon.usageLimitTotal)} (${new Intl.NumberFormat(
    'fr-TN',
    { style: 'percent', maximumFractionDigits: 0 },
  ).format(rate)})`;
}

function formatValue(coupon: Coupon): string {
  return coupon.type === 'percentage'
    ? `${String(coupon.value)} %`
    : formatCatalogPrice(coupon.value);
}

/**
 * "Marketing" — performance réelle des coupons (`useCouponsQuery()`,
 * même endpoint que l'écran Promotions), jamais une seconde source de
 * vérité. "Campagnes" (email/SMS) n'a aucune table ni module dans ce
 * projet — recherché explicitement (`grep -ri campaign`, aucun résultat)
 * — donc absent ici plutôt qu'inventé ; la section l'explique
 * honnêtement au lieu d'un simple silence.
 */
export function MarketingPage(): ReactElement {
  const { t } = useI18n();
  const couponsQuery = useCouponsQuery();
  const coupons = couponsQuery.data ?? [];

  const activeCount = coupons.filter(isCurrentlyActive).length;
  const totalUsage = coupons.reduce((sum, coupon) => sum + coupon.usageCount, 0);
  const topCoupons = [...coupons].sort((a, b) => b.usageCount - a.usageCount).slice(0, 10);

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div>
        <h1 className="sk-page-title">{t('merchant.nav.marketing')}</h1>
        <p className="text-sm text-inkFaint">
          {t('merchant.marketing.subtitlePrefix')}{' '}
          <Link to="/promotions" className="font-medium text-accent hover:underline">
            {t('merchant.nav.promotions')}
          </Link>
          .
        </p>
      </div>

      {couponsQuery.isPending ? (
        <TableSkeleton columns={4} />
      ) : couponsQuery.isError ? (
        <EmptyState title={t('merchant.marketing.loadError')} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            <KpiCard
              label={t('merchant.marketing.activeCoupons')}
              value={String(activeCount)}
              icon={TAG_ICON}
              tone="accent"
              hint={t('merchant.marketing.activeCouponsHint', { total: coupons.length })}
            />
            <KpiCard
              label={t('merchant.marketing.totalUsage')}
              value={String(totalUsage)}
              icon={CHECK_ICON}
              tone="good"
            />
            <KpiCard
              label={t('merchant.marketing.couponsCreated')}
              value={String(coupons.length)}
              icon={RECEIPT_ICON}
              tone="info"
            />
          </div>

          <div>
            <h2 className="mb-3 font-display text-lg font-semibold text-ink">
              {t('merchant.marketing.performanceTitle')}
            </h2>
            {topCoupons.length === 0 ? (
              <EmptyState
                title={t('merchant.marketing.emptyTitle')}
                description={t('merchant.marketing.emptyDescription')}
              />
            ) : (
              <Table caption={t('merchant.marketing.performanceTitle')}>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t('merchant.marketing.table.colCode')}</TableHeaderCell>
                    <TableHeaderCell>{t('merchant.marketing.table.colValue')}</TableHeaderCell>
                    <TableHeaderCell>{t('merchant.orders.table.colStatus')}</TableHeaderCell>
                    <TableHeaderCell align="end">
                      {t('merchant.marketing.table.colUsageRate')}
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topCoupons.map((coupon) => (
                    <TableRow key={coupon.id}>
                      <TableCell>
                        <span className="font-mono font-semibold text-ink">{coupon.code}</span>
                      </TableCell>
                      <TableCell>{formatValue(coupon)}</TableCell>
                      <TableCell>
                        <Badge tone={isCurrentlyActive(coupon) ? 'good' : 'neutral'}>
                          {isCurrentlyActive(coupon)
                            ? t('merchant.categories.active')
                            : t('merchant.categories.inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell align="end">
                        <span className="num text-sm text-ink">{usageRateLabel(coupon, t)}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <div className="rounded-xl2 border border-line bg-surface p-5">
            <h2 className="font-display text-base font-semibold text-ink">
              {t('merchant.marketing.campaignsTitle')}
            </h2>
            <p className="mt-1 text-sm text-inkFaint">
              {t('merchant.marketing.campaignsDescription')}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
